import React, { useMemo } from 'react';
import { TableRow, TableCell, Checkbox, TableHead, Typography } from '@mui/material';
import { AttachmentItem } from '@klab-berlin/api-sdk/lib/types/responses/Common';
import { useTranslation } from 'react-i18next';

import { typography, spaces, palette } from '../../assets/styles/themes/tokens';
import CheckBoxIcon from '../../assets/check-box.svg';
import CheckBoxCheckedIcon from '../../assets/check-box-checked.svg';
import CheckBoxIndeterminateIcon from '../../assets/check-box-indeterminate.svg';

interface AttachmentsTableHeaderProps {
  numSelected: number;
  onClickSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void;
  rowCount: number;
  attachments: AttachmentItem[];
}

export const AttachmentType = {
  FILE: 'file',
  FOLDER: 'folder',
} as const;

export type AttachmentType = typeof AttachmentType[keyof typeof AttachmentType];

const countByType = (attachments: AttachmentItem[], type: AttachmentType): number => {
  return attachments.filter(item => item.type.toLowerCase() === type).length;
};

const AttachmentsTableHeader = ({
  onClickSelectAll,
  numSelected,
  rowCount,
  attachments
}: AttachmentsTableHeaderProps) => {

  const { t } = useTranslation();

  const { folders, files } = useMemo(() => ({
    folders: countByType(attachments, AttachmentType.FOLDER),
    files: countByType(attachments, AttachmentType.FILE),
  }), [attachments]);

  const isIndeterminate = numSelected > 0 && numSelected < rowCount;
  const isAllSelected = rowCount > 0 && numSelected === rowCount;

  return (
    <TableHead sx={{
      display: 'block',
      '& .MuiTableCell-root': {
        borderBottom: 'none',
      },
    }}>
      <TableRow sx={{ paddingLeft: spaces.xs }}>
        <TableCell padding='checkbox'>
          <Checkbox
            data-testid='attachment-table-checkbox-select-all'
            indeterminate={isIndeterminate}
            checked={isAllSelected}
            onChange={onClickSelectAll}
            icon={<CheckBoxIcon />}
            checkedIcon={<CheckBoxCheckedIcon />}
            indeterminateIcon={<CheckBoxIndeterminateIcon />}
            sx={{
              color: palette.action.active,
              borderRadius: spaces.md,
            }}
          />

        </TableCell>
        <TableCell sx={{ paddingLeft: spaces.sm }}>
          <Typography
            sx={{
              ...typography.menu.itemDense,
              fontWeight: '500',
              color: palette.text.primary,
            }}
          >
            {`${folders} ${t('Folders')}, ${files} ${files === 1 ? t('File') : t('Files')}`}
          </Typography>
        </TableCell>
      </TableRow>
    </TableHead>
  );
};

export default AttachmentsTableHeader;
